// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * View builders — the persistent shell, the lobby / pre-join screen and the
 * in-call layout. Pure HTML string builders + a couple of small DOM helpers;
 * all wiring lives in main.ts.
 */
import { icon } from './icons.ts';

export function shellHtml(): string {
  return `
    <div class="app-shell">
      <header class="topbar">
        <a class="brand" href="./" aria-label="Meetwell home">
          <span class="brand-mark">${icon('video')}</span>
          <span class="brand-name">Meetwell</span>
        </a>
        <nav class="topnav">
          <button type="button" class="nav-btn" data-modal="how">${icon('help')}<span>How it works</span></button>
          <button type="button" class="nav-btn" data-modal="privacy">${icon('shield')}<span>Privacy</span></button>
          <button type="button" class="nav-btn" data-modal="about">${icon('info')}<span>About</span></button>
          <button type="button" class="nav-btn icon-only" id="log-toggle" aria-label="Event log" title="Event log">${icon('logDrawer')}</button>
        </nav>
      </header>
      <main class="main-content" id="view"></main>
      <footer class="site-footer">
        <div class="footer-inner">
          <span class="privacy-tag glossary-link" data-term="end-to-end encrypted" tabindex="0" role="button">${icon('shield')} Runs entirely in your browser · nothing hosted</span>
          <span class="foot-credit">Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a></span>
        </div>
      </footer>
    </div>
    <aside class="drawer" id="drawer" hidden aria-label="Event log"></aside>`;
}

export interface LobbyOpts {
  mode: 'create' | 'join';
  room: string;
  name: string;
}

export function lobbyHtml(o: LobbyOpts): string {
  const heading = o.mode === 'create' ? 'Start a private call' : 'Join the call';
  const sub =
    o.mode === 'create'
      ? 'A link is the whole thing. Send it to whoever you want on the call — no account, no download, and no server ever carries your video.'
      : `You’ve been invited to room <code>${escapeHtml(o.room)}</code>. Set up your camera and mic, then join.`;
  const cta = o.mode === 'create' ? 'Start call' : 'Join call';
  return `
    <section class="lobby">
      <div class="lobby-preview">
        <div class="preview-frame">
          <video id="preview-video" class="preview-video" autoplay playsinline muted></video>
          <div class="preview-off" id="preview-off" hidden>
            <div class="avatar-lg" id="preview-avatar">G</div>
            <p>Camera off</p>
          </div>
          <div class="preview-controls">
            <button type="button" class="round-btn" id="pre-mic" aria-pressed="true" title="Microphone">${icon('mic')}</button>
            <button type="button" class="round-btn" id="pre-cam" aria-pressed="true" title="Camera">${icon('video')}</button>
          </div>
        </div>
        <div class="device-row">
          <label class="field">
            <span>${icon('video')} Camera</span>
            <select id="cam-select"><option value="">Default camera</option></select>
          </label>
          <label class="field">
            <span>${icon('mic')} Microphone</span>
            <select id="mic-select"><option value="">Default microphone</option></select>
          </label>
        </div>
        <p class="perm-note" id="perm-note" hidden></p>
      </div>

      <div class="lobby-form">
        <h1>${heading}</h1>
        <p class="lead">${sub}</p>
        <label class="field">
          <span>Your name</span>
          <input type="text" id="name-input" maxlength="40" placeholder="e.g. Alex" value="${escapeHtml(o.name)}" autocomplete="name" />
        </label>
        <button type="button" class="btn-primary btn-lg" id="join-btn">${icon('video')} ${cta}</button>
        <p class="trust-line glossary-link" data-term="full mesh" tabindex="0" role="button">
          ${icon('shield')} End-to-end encrypted · peer-to-peer · nothing touches a server
        </p>
        ${
          o.mode === 'create'
            ? `<button type="button" class="link-btn" id="join-instead">or join a call with a code</button>`
            : `<button type="button" class="link-btn" id="start-instead">or start your own call</button>`
        }
      </div>
    </section>`;
}

export function callHtml(): string {
  return `
    <section class="call">
      <div class="stage">
        <div class="grid" id="grid"></div>
        <div class="sas-bar" id="sas-bar" hidden>
          <span class="sas-label">${icon('shield')}<span class="sas-label-text">Verify aloud</span></span>
          <span class="sas-words" id="sas-words"></span>
          <button type="button" class="icon-btn tiny" data-modal="privacy" title="What is this?">${icon('help')}</button>
        </div>
      </div>

      <aside class="sidepanel" id="sidepanel" hidden>
        <div class="side-tabs">
          <button type="button" class="side-tab on" data-side="people">${icon('users')} People <span id="people-count">1</span></button>
          <button type="button" class="side-tab" data-side="chat">${icon('chat')} Chat</button>
          <button type="button" class="icon-btn side-close" id="side-close" aria-label="Close panel">${icon('x')}</button>
        </div>
        <div class="side-body" id="side-people"></div>
        <div class="side-body" id="side-chat" hidden>
          <div class="chat-log" id="chat-log"></div>
          <form class="chat-form" id="chat-form">
            <input type="text" id="chat-input" placeholder="Message everyone…" maxlength="500" autocomplete="off" />
            <button type="submit" class="icon-btn" aria-label="Send">${icon('send')}</button>
          </form>
        </div>
      </aside>

      <div class="controls" id="controls">
        <div class="ctl-left">
          <span class="call-timer" id="call-timer">00:00</span>
          <button type="button" class="ctl-btn ghost" id="invite-btn">${icon('link')}<span>Invite</span></button>
        </div>
        <div class="ctl-center">
          <button type="button" class="ctl-btn" id="btn-mic" aria-pressed="true">${icon('mic')}<span>Mute</span></button>
          <button type="button" class="ctl-btn" id="btn-cam" aria-pressed="true">${icon('video')}<span>Camera</span></button>
          <button type="button" class="ctl-btn" id="btn-screen">${icon('screen')}<span>Share</span></button>
          <button type="button" class="ctl-btn" id="btn-record">${icon('record')}<span>Record</span></button>
          <button type="button" class="ctl-btn danger" id="btn-leave">${icon('hangup')}<span>Leave</span></button>
        </div>
        <div class="ctl-right">
          <button type="button" class="ctl-btn ghost" id="btn-people">${icon('users')}<span>People</span></button>
          <button type="button" class="ctl-btn ghost" id="btn-chat">${icon('chat')}<span>Chat</span></button>
        </div>
      </div>
    </section>`;
}

/** Build a participant tile element. Returns the tile and its <video>. */
export function makeTile(id: string, label: string, self: boolean): { el: HTMLElement; video: HTMLVideoElement } {
  const el = document.createElement('div');
  el.className = 'tile';
  el.dataset.peer = id;
  const video = document.createElement('video');
  video.autoplay = true;
  (video as HTMLVideoElement & { playsInline: boolean }).playsInline = true;
  video.muted = self; // never play our own audio back
  if (self) video.classList.add('mirror');
  el.appendChild(video);

  const off = document.createElement('div');
  off.className = 'tile-off';
  off.innerHTML = `<div class="avatar">${escapeHtml(initials(label))}</div>`;
  el.appendChild(off);

  const badge = document.createElement('div');
  badge.className = 'tile-badge';
  badge.innerHTML = `<span class="tile-mic">${icon('mic')}</span><span class="tile-name"></span>`;
  (badge.querySelector('.tile-name') as HTMLElement).textContent = self ? `${label} (you)` : label;
  el.appendChild(badge);

  const rec = document.createElement('div');
  rec.className = 'tile-rec';
  rec.hidden = true;
  rec.innerHTML = `${icon('record')} REC`;
  el.appendChild(rec);

  return { el, video };
}

export function initials(name: string): string {
  const parts = (name || 'Guest').trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || 'G';
}

export function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
